import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string, inviteRole?: string, userType?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Keep the user object reference stable when the id is unchanged. Supabase
    // re-validates the session on tab refocus and emits TOKEN_REFRESHED/SIGNED_IN
    // with a *new* user object each time. Replacing `user` on every such event
    // cascades into profile refetches and unmounts the active page (e.g. a live
    // roleplay session). Only swap the reference on a genuine user change.
    const applyUser = (nextUser: User | null) => {
      setUser((prev) => (prev?.id === nextUser?.id ? prev : nextUser));
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        applyUser(session?.user ?? null);
        setLoading(false);

        // Handle pending team invitation after signup confirmation
        if (event === 'SIGNED_IN' && session?.user) {
          setTimeout(async () => {
            const pendingInvitationToken = localStorage.getItem('pending_team_invitation');
            if (pendingInvitationToken) {
              try {
                const { data, error } = await supabase.functions.invoke('accept-team-invitation', {
                  body: { token: pendingInvitationToken },
                  headers: {
                    Authorization: `Bearer ${session.access_token}`,
                  },
                });

                if (!error && data?.accepted) {
                  console.log('Pending team invitation accepted:', data.teamName);
                  localStorage.removeItem('pending_team_invitation');
                  // Redirect will happen via the AcceptInvitation page or normal flow
                }
              } catch (err) {
                console.error('Error accepting pending invitation:', err);
              }
            }
          }, 0);

          // Handle pending license invite code (from /join/:code redirect or signup)
          setTimeout(async () => {
            try {
              const { redeemPendingInvite } = await import('@/lib/redeemPendingInvite');
              const r = await redeemPendingInvite();
              if (!r.ranRpc) return;
              if (r.ok && !r.result?.requires_payment) {
                const p = window.location.pathname;
                if (p === '/' || p === '/auth' || p === '/no-license') {
                  window.location.assign('/dashboard');
                }
                return;
              }
              if (!r.ok && !r.silent) {
                const { toast } = await import('@/hooks/use-toast');
                toast({
                  title: "Couldn't apply invite code",
                  description: r.error || 'Please contact support.',
                  variant: 'destructive',
                });
              }
            } catch (err) {
              console.error('Error redeeming pending invite code:', err);
            }
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      applyUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string, inviteRole?: string, userType?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const metadata: Record<string, string> = {};
    
    // Add full_name to metadata if provided
    if (fullName) {
      metadata.full_name = fullName;
    }
    
    // Add invite_role to metadata if provided
    if (inviteRole) {
      metadata.invite_role = inviteRole;
    }
    
    // Add user_type to metadata if provided
    if (userType) {
      metadata.user_type = userType;
    }
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata,
      },
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out failed:', error.message);
      }
    } catch (err) {
      console.error('Sign out exception:', err);
    } finally {
      setUser(null);
      setSession(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
